import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
// reactstrap components
import {
  Button,
  Card,
  CardHeader,
  CardFooter,
  CardBody,
  Progress,
} from "reactstrap";
// core components
import { serverHost } from "variables/serverdata.js";
import WelcomeStep from "./Steps/WelcomeStep";
import SiteStep from "./Steps/SiteStep";
import DomainStep from "./Steps/DomainStep";
import UserStep from "./Steps/UserStep";
import ReviewStep from "./Steps/ReviewStep";
import WizardFinish from "./WizardFinish";

const SiteSetupWizard = () => {
  const navigate = useNavigate();
  const [domainExists, setDomainExists] = useState(false);
  const [formData, setFormData] = useState({
    site_information: {
      company_name: "",
      address: "",
      city: "",
      country: "",
      postal_code: "",
    },
    domain_detail: {
      domain_name: "",
      modules: [],
    },
    admin_user: {
      new_user: false,
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      role: "admin",
      modules: [],
    },
  });
  const steps = [
    { id: "Welcome", component: WelcomeStep },
    { id: "Site Information", component: SiteStep },
    { id: "Domain Information", component: DomainStep },
    { id: "New Admin User", component: UserStep },
    { id: "Review", component: ReviewStep },
  ];
  const [completed, setCompleted] = useState(false);
  const [currentStep, setCurrentStep] = useState(0); // Current step based on array index of steps
  const [error, setError] = useState();
  const [newSite, setNewSite] = useState("");
  const [assignedClient, setClient] = useState();
  const progressPercent = ((currentStep + 1) / steps.length) * 100;
  const CurrentStepComponent = steps[currentStep].component;
  const updateSection = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  };
  const goToStep = (index) => setCurrentStep(index);

  const handleSubmit = async () => {
    setError("");
    let url = `${serverHost}/site_wizard`;
    try {
      if (
        !formData.site_information.company_name ||
        !formData.site_information.address ||
        !formData.site_information.city ||
        !formData.site_information.country ||
        !formData.site_information.postal_code ||
        (formData.admin_user.new_user &&
          (!formData.admin_user.first_name ||
            !formData.admin_user.last_name ||
            !formData.admin_user.email ||
            !formData.admin_user.phone))
      ) {
        setError("Please fill in all required fields");
        return;
      }
      const client_uuid = assignedClient
        ? assignedClient
        : localStorage.getItem("clientId");
      const user = JSON.parse(localStorage.getItem("user"));
      const payload = {
        data: formData,
        client_id: client_uuid,
        insert: user.user_uuid,
      };
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      if (response.ok) {
        const data = await response.json();
        setNewSite(data.site_uuid);
        setCompleted(true);
      } else {
        const errData = await response.json();
        console.error("Failed to create site:", errData);
        setError(errData.message || "Failed to create site. Please try again.");
      }
    } catch (err) {
      console.error("Error Creating site: ", err);
      setError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <>
      <Card className="mb-4" style={{ maxWidth: "70%", margin: "0 auto" }}>
        {error && (
          <div style={{ maxWidth: "70%", margin: "0 auto 1rem" }}>
            <div
              style={{
                backgroundColor: "#fa2b3c",
                color: "#842029",
                border: "1px solid #f5c2c7",
                borderRadius: "0.375rem",
                padding: "0.75rem 1.25rem",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span>{error}</span>
              <button
                onClick={() => setError("")}
                style={{
                  background: "none",
                  border: "none",
                  fontSize: "1.25rem",
                  lineHeight: 1,
                  color: "#000000",
                  cursor: "pointer",
                  padding: "0 0 0 1rem",
                }}
                aria-label="Close"
              >
                ×
              </button>
            </div>
          </div>
        )}
        <CardHeader
          style={{
            backgroundColor: "#172b4d",
          }}
        >
          <div className="d-flex justify-content-between mb-2">
            <span>
              Step {currentStep + 1} of {steps.length}
            </span>

            <span>{steps[currentStep].id}</span>
          </div>

          <Progress value={progressPercent} />
        </CardHeader>
        {completed ? (
          <>
            <CardBody>
              <WizardFinish
                navigate={navigate}
                formData={formData}
                site_uuid={newSite}
              />
            </CardBody>
          </>
        ) : (
          <>
            <CardBody>
              <CurrentStepComponent
                formData={formData}
                updateSection={updateSection}
                goToStep={goToStep}
                client={assignedClient}
                selectClient={setClient}
                domainExists={domainExists}
                setDomainExists={setDomainExists}
              />
            </CardBody>
            <CardFooter style={{ backgroundColor: "transparent" }}>
              <div className="d-flex justify-content-between">
                <Button
                  color="secondary"
                  disabled={currentStep === 0}
                  onClick={prevStep}
                >
                  <span
                    style={{
                      color: "black",
                    }}
                  >
                    Back
                  </span>
                </Button>

                {currentStep === steps.length - 1 ? (
                  <Button 
                    color="success" 
                    onClick={handleSubmit}
                  >
                    Create Site
                  </Button>
                ) : (
                  <Button
                    color="primary"
                    onClick={nextStep}
                    disabled={domainExists || (currentStep === 0 && !assignedClient)}
                  >
                    Next
                  </Button>
                )}
              </div>
            </CardFooter>
          </>
        )}
      </Card>
    </>
  );
};

export default SiteSetupWizard;
